import { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, alpha } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import HistoryIcon from '@mui/icons-material/History';
import InsightsIcon from '@mui/icons-material/Insights';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { InventoryList } from './list';
import { InventoryMovements } from './movements';
import { StockForecast } from './forecast';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useTierAccess } from '../../hooks/useTierAccess';
import { UpgradeModal } from '../../components/UpgradeModal';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const InventoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useContext(ColorModeContext);
  const { tier, hasTierAccess } = useTierAccess();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Get tab from URL, default to 'stock'
  const tabParam = searchParams.get('tab');
  const getTabIndex = (tab: string | null): number => {
    switch (tab) {
      case 'movements':
        return 1;
      case 'forecast':
        return 2;
      default:
        return 0;
    }
  };

  const [tabValue, setTabValue] = useState(getTabIndex(tabParam));

  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getTabIndex(tabParam));
  }, [tabParam]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // Check if forecast tab requires PRO tier
    if (newValue === 2 && !hasTierAccess('professional')) {
      setUpgradeModalOpen(true);
      return;
    }

    const tabNames = ['stock', 'movements', 'forecast'];
    setSearchParams({ tab: tabNames[newValue] });
    setTabValue(newValue);
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textMuted: palette.light.textMuted,
  };

  const isForecastLocked = !hasTierAccess('professional');

  return (
    <Box>
      {/* Tabs Header */}
      <Box
        sx={{
          mb: 3,
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Inventory tabs"
          sx={{
            minHeight: 56,
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 3,
            },
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'uppercase',
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.widest,
              color: colors.textMuted,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(palette.primary, 0.05),
                color: colors.textPrimary,
              },
              '&.Mui-selected': {
                color: palette.primary,
                backgroundColor: alpha(palette.primary, 0.1),
              },
            },
          }}
        >
          <Tab
            icon={<InventoryIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Stock Levels"
            id="inventory-tab-0"
            aria-controls="inventory-tabpanel-0"
          />
          <Tab
            icon={<HistoryIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Movements"
            id="inventory-tab-1"
            aria-controls="inventory-tabpanel-1"
          />
          <Tab
            icon={isForecastLocked ? <LockOutlinedIcon sx={{ fontSize: 18, color: palette.warning }} /> : <InsightsIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Forecast"
            id="inventory-tab-2"
            aria-controls="inventory-tabpanel-2"
            sx={isForecastLocked ? {
              opacity: 0.6,
              '&:hover': {
                backgroundColor: alpha(palette.warning, 0.1),
              },
            } : {}}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <InventoryList />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <InventoryMovements />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <StockForecast />
      </TabPanel>

      {/* Upgrade Modal */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        feature="Stock Forecast"
        currentTier={tier}
        requiredTier="professional"
      />
    </Box>
  );
};
